/**
 * Classe base abstrata para estratégias de processamento de relatórios.
 * Cada subtipo de relatório (Contas a Receber, Pagar, etc.) deve estender
 * esta classe e implementar os métodos:
 *   - processar(dadosBrutos)
 *   - getFormComponent()
 * 
 * O objetivo é receber os dados crus da API e retornar um objeto padronizado
 * com a estrutura que o componente visual (<RelatorioTemplate/>) espera.
 * 
 * Princípio Aberto/Fechado (OCP):
 * - A base define o contrato (métodos abstratos)
 * - Cada Strategy implementa seu próprio processamento E seu próprio formulário
 * - O index.js não precisa de switch/if para decidir qual formulário renderizar
 */
export default class RelatorioStrategyBase {
  /**
   * @param {Object} opcoes - Parâmetros globais como moeda, mostrarAmbasMoedas, etc.
   * @param {number} opcoes.moeda - Moeda alvo (5=BRL, 6=USD)
   * @param {boolean} opcoes.mostrarAmbasMoedas - Se deve exibir BRL e USD
   * @param {string} opcoes.por - Tipo de agrupamento (porCliente, porVencimento, etc.)
   */
  constructor(opcoes = {}) {
    this.opcoes = opcoes;
  }

  /**
   * Método abstrato: processa os dados brutos da API.
   * DEVE ser sobrescrito por todas as subclasses.
   * 
   * @param {Array|Object} dadosBrutos - Resposta da API
   * @returns {Object} { titulo: string, grupos: Array, totaisGerais: Object }
   */
  processar(dadosBrutos) {
    throw new Error("O método processar() deve ser implementado pela subclasse.");
  }

  /**
   * Método abstrato: retorna o componente de formulário de filtros.
   * DEVE ser sobrescrito por todas as subclasses.
   * 
   * Princípio Aberto/Fechado: cada Strategy sabe qual formulário lhe pertence.
   * O index.js não precisa de switch — apenas chama strategy.getFormComponent().
   * 
   * @returns {React.Component} Referência do componente de formulário
   */
  getFormComponent() {
    throw new Error("O método getFormComponent() deve ser implementado pela subclasse.");
  }

  /**
   * Helper: calcula valores convertidos com base no ROE (taxa de câmbio).
   * Centraliza a lógica de conversão BRL ↔ USD.
   * 
   * @param {Object} conta - Objeto da conta (normal ou manual)
   * @returns {Object} Valores convertidos
   */
  aplicarConversaoMoeda(conta) {
    const { moeda, mostrarAmbasMoedas } = this.opcoes;
    const moedaConta = parseInt(conta?.moeda || 5);
    const roe = parseFloat(conta?.roe || conta?.os_roe || 5) || 1;

    let bruto = parseFloat(conta?.bruto || 0);
    let retencao = parseFloat(conta?.retencao || 0);
    let descontos = parseFloat(conta?.descontos || conta?.discount || 0);
    let pago = parseFloat(conta?.pago || conta?.received || conta?.valorPago || 0);
    let saldo = parseFloat(conta?.saldo || conta?.balance || conta?.liquidoPagar || 0);

    // Se as moedas são diferentes, converte
    if (parseInt(moeda) !== moedaConta) {
      if (parseInt(moeda) === 5) {
        // Convertendo de USD para BRL (multiplica pelo ROE)
        bruto = conta?.brutoDOLAR || bruto * roe;
        retencao = conta?.retencaoDOLAR || retencao * roe;
        descontos = conta?.discountDOLAR || descontos * roe;
        pago = conta?.receivedDOLAR || pago * roe;
        saldo = conta?.balanceDolar || saldo * roe;
      } else {
        // Convertendo de BRL para USD (divide pelo ROE)
        bruto = conta?.brutoDOLAR || bruto / roe;
        retencao = conta?.retencaoDOLAR || retencao / roe;
        descontos = conta?.discountDOLAR || descontos / roe;
        pago = conta?.receivedDOLAR || pago / roe;
        saldo = conta?.balanceDolar || saldo / roe;
      }
    }

    // Calcula os valores puros para USD caso a visualização exija ambas as moedas
    // IMPORTANTE: Fazemos isso ANTES de deduzir a variação cambial do bruto, 
    // para que o valor em USD não sofra alteração.
    let brutoUsd, retencaoUsd, descontosUsd, pagoUsd, saldoUsd;
    if (mostrarAmbasMoedas) {
      const divisor = parseInt(moeda) === 5 ? roe : 1;
      brutoUsd = parseFloat((bruto / divisor).toFixed(2));
      retencaoUsd = parseFloat((retencao / divisor).toFixed(2));
      descontosUsd = parseFloat((descontos / divisor).toFixed(2));
      pagoUsd = parseFloat((pago / divisor).toFixed(2));
      saldoUsd = parseFloat((saldo / divisor).toFixed(2));
    }

    // APLICAÇÃO DO ROE ANTIGO:
    // Se estamos visualizando em Reais (moeda 5) e existe variação cambial (contas liquidadas),
    // deduzimos a variação cambial do bruto convertido para encontrar o valor com o ROE antigo.
    if (parseInt(moeda) === 5 && conta?.variacao_cambial) {
      bruto = bruto - parseFloat(conta.variacao_cambial);
    }

    const resultado = {
      bruto: parseFloat(bruto.toFixed(2)),
      retencao: parseFloat(retencao.toFixed(2)),
      descontos: parseFloat(descontos.toFixed(2)),
      pago: parseFloat(pago.toFixed(2)),
      saldo: parseFloat(saldo.toFixed(2)),
    };

    if (mostrarAmbasMoedas) {
      resultado.brutoUsd = brutoUsd;
      resultado.retencaoUsd = retencaoUsd;
      resultado.descontosUsd = descontosUsd;
      resultado.pagoUsd = pagoUsd;
      resultado.saldoUsd = saldoUsd;
    }

    return resultado;
  }

  /**
   * Helper: formata uma linha padronizada da tabela.
   * Usado por subclasses para criar cada linha da tabela unificada.
   * 
   * @param {Object} conta - Dados brutos da conta
   * @returns {Object} Linha padronizada
   */
  criarLinhaTabela(conta) {
    const valores = this.aplicarConversaoMoeda(conta);
    return {
      vencimento: conta?.vencimento || conta?.dataPagamento || "",
      os: conta?.os || conta?.os_codigo || conta?.st || "",
      documento: conta?.documento || conta?.Docto || "",
      navio: conta?.navio || conta?.navio_nome || conta?.navio_manual || conta?.manual_navio_nome || "N/A",
      moeda: conta?.moeda || 5,
      roe: parseFloat(conta?.roe || conta?.os_roe || 5),
      variacao_cambial: parseFloat(conta?.variacao_cambial || 0),
      variacao_cambialUsd: parseFloat(conta?.variacao_cambialUsd || 0),
      ...valores,
    };
  }
}